import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import Seo from '@/components/Seo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Modules from '@/components/Modules';
import StoreBadges from '@/components/StoreBadges';
import SectionEyebrow from '@/components/SectionEyebrow';
import { FadeIn } from '@/components/motion-primitives';

/* =====================================================================
   Features (Özellikler) — YENİ SAYFA  → route: /ozellikler
   Mevcut <Modules /> bileşenini yeniden kullanır; üstüne hero, entegrasyon
   anlatımı, mobil uygulama bölümü ve kapanış CTA'sı ekler.
   ===================================================================== */

const INTEGRATION_POINTS = [
  'QR menüden gelen sipariş, sipariş yönetimine anında düşer',
  'Her satış, sadakat puanı ve raporlamaya otomatik işlenir',
  'Ürün ve fiyat değişikliği tüm kanallarda aynı anda geçerli olur',
  'Personel yetkileri tüm modüllerde tek yerden yönetilir',
];

const Features = () => (
  <div className="min-h-screen bg-surface">
    <Seo
      title="Özellikler & Modüller — Tilki Otomasyon"
      description="QR menü, sipariş yönetimi, sadakat sistemi, raporlama ve daha fazlası. Tilki Otomasyon'un sekiz modülü tek panelde, birbiriyle entegre çalışır."
      path="/ozellikler"
    />
    <Header />

    <main>
      {/* Hero */}
      <section className="bg-surface pb-14 pt-16 sm:pb-16 sm:pt-20">
        <div className="container mx-auto px-4">
          <FadeIn className="mx-auto max-w-3xl text-center">
            <SectionEyebrow className="mb-4">Özellikler</SectionEyebrow>
            <h1 className="type-display-2 text-balance mb-4">İşletmenizin ihtiyacı olan her modül</h1>
            <p className="type-body-lg text-pretty mx-auto max-w-2xl">
              QR menüden sadakat sistemine, sipariş takibinden raporlamaya kadar tüm süreçler tek platformda, birbiriyle
              entegre çalışır.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/demo" className="modern-btn-large">
                Ücretsiz Demo Al
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
              <Link to="/fiyatlar" className="modern-btn-outline">
                Fiyatları Gör
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Mevcut modül ızgarası aynen yeniden kullanılıyor */}
      <Modules />

      {/* Entegrasyon */}
      <section className="section-y bg-surface">
        <div className="container mx-auto px-4">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <FadeIn>
              <SectionEyebrow className="mb-4">Entegre Çalışır</SectionEyebrow>
              <h2 className="type-h2 text-balance mb-4">Veriler modüller arasında otomatik akar</h2>
              <p className="type-body-lg mb-7">
                Ayrı ayrı sistemlerle uğraşmayın. Bir modülde oluşan veri diğerlerinde anında güncellenir — çift veri
                girişi, tutarsız rapor ve kayıp sipariş derdi ortadan kalkar.
              </p>
              <ul className="space-y-3.5">
                {INTEGRATION_POINTS.map((point) => (
                  <li key={point} className="flex items-start gap-2.5">
                    <CheckCircle className="mt-0.5 h-[18px] w-[18px] flex-shrink-0 text-brand-600" aria-hidden="true" />
                    <span className="font-inter text-[15px] text-ink-muted">{point}</span>
                  </li>
                ))}
              </ul>
            </FadeIn>

            <FadeIn delay={0.1}>
              <div className="rounded-2xl border border-hairline bg-surface p-3 shadow-card-lift">
                <img
                  src="/img/tilki-hero.jpg"
                  alt="Tilki Otomasyon modül paneli"
                  width="591"
                  height="309"
                  loading="lazy"
                  decoding="async"
                  className="h-64 w-full rounded-xl object-cover sm:h-72"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Mobil uygulamalar */}
      <section className="section-y bg-ink-strong">
        <div className="container mx-auto px-4">
          <FadeIn className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="mb-3 font-poppins text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Mobil uygulamalarımız
            </h2>
            <p className="font-inter text-white/60">Yönetici paneli ve müşteri sadakat uygulaması iOS ve Android'de.</p>
          </FadeIn>

          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-white/12 bg-white/5 p-7">
              <h3 className="mb-1.5 font-poppins text-xl font-semibold text-white">Yönetici Paneli</h3>
              <p className="mb-5 font-inter text-sm text-white/60">Restoran sahipleri ve yöneticiler için</p>
              <StoreBadges apps="admin" tone="dark" />
            </div>

            <div className="rounded-2xl border border-white/12 bg-white/5 p-7">
              <h3 className="mb-1.5 font-poppins text-xl font-semibold text-white">Sadakat Uygulaması</h3>
              <p className="mb-5 font-inter text-sm text-white/60">Müşterileriniz için puan ve kampanya</p>
              <StoreBadges apps="loyalty" tone="dark" />
            </div>
          </div>
        </div>
      </section>

      {/* Kapanış CTA */}
      <section className="section-y border-t border-hairline bg-surface-2">
        <div className="container mx-auto px-4">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <h2 className="type-h2 text-balance mb-3.5">Hangi modüllerin size uygun olduğunu birlikte belirleyelim</h2>
            <p className="type-body-lg mb-8">Ücretsiz demo talep edin, işletmenize özel kurulumu gösterelim.</p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/demo" className="modern-btn-large">
                Ücretsiz Demo Al
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
              <Link to="/iletisim" className="modern-btn-secondary">
                İletişime Geç
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default Features;
